/* Central Communication History — thin API client for the Pass D UI.
 *
 * All data is drawn server-side from the `emails` and `sms_messages`
 * audit tables via CommunicationHistoryController. `delivered` is
 * never set by this project for either channel — no verified
 * provider callback exists (see Pass D §11, §14). */

import { API_URL } from "../config";
import { trackedFetch } from "../Components/Loader/GlobalLoader";

const headers = (): HeadersInit => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export type HistoryChannel = "email" | "sms";
export type HistoryStatus  = "queued" | "sent" | "delivered" | "failed" | "suppressed" | "cancelled";

export interface HistoryRow {
  id: number;
  channel: HistoryChannel;
  campaign_id: number | null;
  recipient_address: string | null;
  subject: string | null;
  body_preview: string | null;
  body: string | null;
  sender: string | null;
  status: HistoryStatus;
  provider: "microsoft" | "intalk" | null;
  provider_message_id: string | null;
  is_test: boolean;
  opened_at: string | null;
  last_opened_at: string | null;
  open_count: number;
  failed_at: string | null;
  failure_reason: string | null;
  created_at: string;
}

export interface HistoryFilters {
  search?: string;
  channel?: HistoryChannel;
  status?: HistoryStatus;
  campaign_id?: number;
  date_from?: string;
  date_to?: string;
  include_test?: boolean;
  page?: number;
  per_page?: number;
}

interface Envelope<T> { code: number; status: boolean; message?: string; data: T; }

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res  = await trackedFetch(`${API_URL}${path}`, { ...init, headers: headers() });
  const json: Envelope<T> = await res.json();
  if (! json.status) throw new Error(json.message || "Request failed");
  return json.data;
}

export const listHistory = (f: HistoryFilters = {}) => {
  const qs = new URLSearchParams();
  for (const k of Object.keys(f) as Array<keyof HistoryFilters>) {
    const v = f[k];
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return call<{
    data: HistoryRow[]; total: number;
    per_page: number; current_page: number; last_page: number;
  }>(`/communication/history${suffix}`, { method: "GET" });
};

export const fetchHistoryItem = (channel: HistoryChannel, id: number) =>
  call<HistoryRow>(`/communication/history/${channel}/${id}`, { method: "GET" });

/* ── Profile timelines ────────────────────────────────────────────
 * Scoped server-side to addresses the profile actually owns —
 * unrelated recipient communications never appear here. */
export const studentTimeline = (studentId: number) =>
  call<{ data: HistoryRow[]; total: number }>(
    `/students/${studentId}/communication-history`, { method: "GET" });

export const parentTimeline = (parentId: number) =>
  call<{ data: HistoryRow[]; total: number }>(
    `/parents/${parentId}/communication-history`, { method: "GET" });

export const staffTimeline = (staffId: number) =>
  call<{ data: HistoryRow[]; total: number }>(
    `/staff/${staffId}/communication-history`, { method: "GET" });
